<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { ChevronDown } from 'lucide-vue-next'
import { cn } from '@/lib/utils'
import { ScrollArea } from '@/components/ui/scroll-area'
import { usePermissions } from '@/composables/usePermissions'
import { navItems, type NavItem } from './navItems'

const isHashAction = (to?: string) => to && to.startsWith('#')

const props = defineProps<{
  collapsed: boolean
}>()

const emit = defineEmits<{
  (e: 'navigate'): void
  (e: 'expand'): void
}>()

const route = useRoute()
const { can } = usePermissions()

const openGroups = ref<Set<string>>(new Set())

const canSee = (item: NavItem) => {
  if (!item.permission) return true
  return can(item.permission)
}

const canSeeChild = (child: NonNullable<NavItem['children']>[number]) => {
  if (!child.permission) return true
  return can(child.permission)
}

const canSeeChildren = (item: NavItem) =>
  item.children?.some((c) => canSeeChild(c)) ?? false

const filteredItems = computed(() =>
  navItems.filter((item) => canSee(item) || canSeeChildren(item)).map((item) => ({
    ...item,
    children: item.children?.filter(canSeeChild) ?? [],
  }))
)

const activePath = computed(() => route.path)

const isItemActive = (item: NavItem) => {
  if (item.to) return activePath.value === item.to
  if (item.children) return item.children.some((c) => activePath.value === c.to)
  return false
}

const toggleGroup = (label: string) => {
  if (props.collapsed) {
    emit('expand')
    if (!openGroups.value.has(label)) {
      openGroups.value.add(label)
    }
    return
  }
  if (openGroups.value.has(label)) {
    openGroups.value.delete(label)
  } else {
    openGroups.value.add(label)
  }
}

watch(
  activePath,
  (path) => {
    for (const item of navItems) {
      if (item.children?.some((c) => c.to === path)) {
        openGroups.value.add(item.label)
      }
    }
  },
  { immediate: true }
)

const handleClick = (item: NavItem) => {
  if (item.external) {
    window.open('https://cti-adeslas.my3cx.es', '_blank')
    emit('navigate')
    return
  }
  emit('navigate')
}

const handleChildClick = (child: NonNullable<NavItem['children']>[number], event: MouseEvent) => {
  if (child.to === '#start-tour') {
    event.preventDefault()
    window.dispatchEvent(new CustomEvent('tour:start-current'))
    emit('navigate')
    return
  }
  if (child.to === '#reset-tours') {
    event.preventDefault()
    localStorage.removeItem('toursCompleted')
    window.dispatchEvent(new CustomEvent('tour:reset'))
    emit('navigate')
    return
  }
  emit('navigate')
}
</script>

<template>
  <aside
    :class="cn(
      'flex h-full w-full flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground',
    )"
  >
    <!-- Logo -->
    <div class="flex h-16 shrink-0 items-center justify-center border-b border-sidebar-border bg-white px-2 dark:bg-sidebar"
    >
      <img
        src="@/assets/combined_logo.png"
        alt="CTI CRM"
        :class="cn('object-contain transition-all max-w-full', props.collapsed ? 'h-9 w-auto' : 'h-11 w-auto')"
      />
    </div>

    <!-- Navigation -->
    <ScrollArea class="min-h-0 flex-1">
      <nav class="flex flex-col gap-1 p-2">
        <template v-for="item in filteredItems" :key="item.label">
          <!-- External link -->
          <a
            v-if="item.external"
            href="https://cti-adeslas.my3cx.es"
            target="_blank"
            rel="noopener noreferrer"
            :class="cn(
              'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
              'text-sidebar-foreground/80'
            )"
            @click="emit('navigate')"
          >
            <component :is="item.icon" class="size-5 shrink-0" />
            <span v-if="!collapsed" class="truncate">{{ item.label }}</span>
          </a>

          <!-- Single link -->
          <RouterLink
            v-else-if="item.to && !item.children?.length"
            :to="item.to"
            :class="cn(
              'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
              isItemActive(item)
                ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                : 'text-sidebar-foreground/80'
            )"
            @click="handleClick(item)"
          >
            <component :is="item.icon" class="size-5 shrink-0" />
            <span v-if="!collapsed" class="truncate">{{ item.label }}</span>
          </RouterLink>

          <!-- Group with children -->
          <div v-else-if="item.children?.length" class="flex flex-col">
            <button
              type="button"
              :class="cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                isItemActive(item)
                  ? 'text-sidebar-foreground'
                  : 'text-sidebar-foreground/80'
              )"
              @click="toggleGroup(item.label)"
            >
              <component :is="item.icon" class="size-5 shrink-0" />
              <span v-if="!collapsed" class="flex-1 truncate text-left">{{ item.label }}</span>
              <ChevronDown
                v-if="!collapsed"
                class="size-4 shrink-0 transition-transform"
                :class="openGroups.has(item.label) ? 'rotate-180' : ''"
              />
            </button>

            <!-- Children -->
            <div
              v-if="!collapsed && openGroups.has(item.label)"
              class="ml-4 flex flex-col gap-1 border-l border-sidebar-border pl-2 pt-1"
            >
              <template v-for="child in item.children" :key="child.to">
                <button
                  v-if="isHashAction(child.to)"
                  type="button"
                  :class="cn(
                    'flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left text-sm transition-colors',
                    'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                    activePath === child.to
                      ? 'bg-sidebar-primary/10 font-medium text-sidebar-primary'
                      : 'text-sidebar-foreground/70'
                  )"
                  @click="(e: MouseEvent) => handleChildClick(child, e)"
                >
                  <span class="size-1.5 rounded-full bg-current opacity-60" />
                  {{ child.label }}
                </button>

                <RouterLink
                  v-else
                  :to="child.to"
                  :class="cn(
                    'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors',
                    'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                    activePath === child.to
                      ? 'bg-sidebar-primary/10 font-medium text-sidebar-primary'
                      : 'text-sidebar-foreground/70'
                  )"
                  @click="(e: MouseEvent) => handleChildClick(child, e)"
                >
                  <span class="size-1.5 rounded-full bg-current opacity-60" />
                  {{ child.label }}
                </RouterLink>
              </template>
            </div>
          </div>
        </template>
      </nav>
    </ScrollArea>
  </aside>
</template>